import * as React from "react"

const HEADER_HEIGHTS = {
  CART_TOAST_WITH_PROMO: 120,
  COUNTDOWN_TIMER: 56,
  DESKTOP_COLLAPSED: 64,
  DESKTOP_FULL: 88,
  PROMO_BANNER: 36,
} as const

type Opts = {
  collapseThreshold?: number // px scrolled
  hasCountdownTimer: boolean
  promosCount: number
  shortenThreshold?: number // px scrolled
  showPromoBanner: boolean
}

export default function useHeaderDynamics({
  collapseThreshold = 50,
  hasCountdownTimer,
  promosCount,
  shortenThreshold = 50,
  showPromoBanner,
}: Opts) {
  const [shortenDesktopHeader, setShorten] = React.useState(false)
  const [collapsePromoBanner, setCollapse] = React.useState(false)
  const rafRef = React.useRef<null | number>(null)

  React.useEffect(() => {
    const onScroll = () => {
      if (rafRef.current != null) {
        return
      }
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        const y = window.scrollY || document.documentElement.scrollTop || 0
        setShorten(y > shortenThreshold)
        if (showPromoBanner && promosCount > 0) {
          setCollapse(y > collapseThreshold)
        } else {
          setCollapse(false)
        }
      })
    }

    onScroll() // initialize state
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [showPromoBanner, promosCount, shortenThreshold, collapseThreshold])

  // derived values to keep math centralized
  const megaMenuOffset = React.useMemo(() => {
    const base = shortenDesktopHeader
      ? HEADER_HEIGHTS.DESKTOP_COLLAPSED
      : HEADER_HEIGHTS.DESKTOP_FULL + HEADER_HEIGHTS.PROMO_BANNER

    return hasCountdownTimer ? base + HEADER_HEIGHTS.COUNTDOWN_TIMER : base
  }, [hasCountdownTimer, shortenDesktopHeader])

  const cartToastYPosition = React.useMemo(() => {
    if (shortenDesktopHeader) return HEADER_HEIGHTS.DESKTOP_COLLAPSED
    if (showPromoBanner) return HEADER_HEIGHTS.CART_TOAST_WITH_PROMO
    return HEADER_HEIGHTS.DESKTOP_FULL
  }, [shortenDesktopHeader, showPromoBanner])

  return {
    cartToastYPosition,
    collapsePromoBanner,
    megaMenuOffset,
    shortenDesktopHeader,
  }
}
